import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Sprout, Droplet, Sun, Wind } from "lucide-react";

const HeroSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  return (
    <section id="home" className="relative bg-primary overflow-hidden">
      <motion.div 
        className="absolute inset-0 z-0"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <motion.img 
          src="/images/wheat-field-sunset.jpg" 
          alt="Agricultural landscape with wheat fields at sunset" 
          className="h-full w-full object-cover opacity-30"
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5 }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-primary/40"></div>
      </motion.div>
      
      <motion.div
        className="absolute top-10 right-10 z-10 md:block hidden"
        initial={{ opacity: 0, y: -50, rotate: -5 }}
        animate={{ opacity: 0.9, y: 0, rotate: 0 }}
        transition={{ duration: 0.8, delay: 0.7 }}
      >
        <img 
          src="/images/varsha-seeds-logo.png" 
          alt="Varsha Seeds Logo" 
          className="w-40 h-auto"
        />
      </motion.div>
      
      <div className="container mx-auto px-4 py-20 md:py-32 relative z-10" ref={ref}>
        <motion.div 
          className="max-w-3xl"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.span 
            className="inline-block bg-accent text-white px-4 py-1 rounded-full text-sm font-medium mb-4"
            initial={{ opacity: 0, x: -20 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Premier Seed Manufacturer
          </motion.span>
          
          <motion.h2 
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white font-heading leading-tight mb-6"
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            Growing Tomorrow's Success With Today's Seeds
          </motion.h2>
          
          <motion.p 
            className="text-white text-lg md:text-xl mb-8 max-w-2xl"
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            Pioneering agricultural solutions with premium quality seeds and 
            innovative processing technologies for sustainable farming.
          </motion.p>
          
          <motion.div 
            className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4"
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <Link href="/products">
              <Button variant="secondary" size="lg" className="rounded-full bg-white hover:bg-gray-100 text-primary">
                Explore Products
              </Button>
            </Link>
            <Link href="/contact">
              <Button size="lg" className="rounded-full bg-accent hover:bg-accent/90 text-white">
                Contact Us
              </Button>
            </Link>
          </motion.div>
        </motion.div>
        
        <motion.div 
          className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-3xl"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.5, delay: 0.7 }}
        >
          {[
            { icon: <Sprout className="h-6 w-6 text-white" />, label: "Superior Germination" },
            { icon: <Droplet className="h-6 w-6 text-white" />, label: "Water Efficient" },
            { icon: <Sun className="h-6 w-6 text-white" />, label: "Climate Resistant" },
            { icon: <Wind className="h-6 w-6 text-white" />, label: "Pest Protected" }
          ].map((item, index) => (
            <motion.div 
              key={index}
              className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center"
              whileHover={{ y: -5, backgroundColor: "rgba(255, 255, 255, 0.2)" }}
              transition={{ duration: 0.2 }}
            >
              <div className="bg-primary/20 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2">
                {item.icon}
              </div>
              <p className="text-white text-sm font-medium">{item.label}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
      
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-background to-transparent"></div>
      
      {/* Animated elements */}
      <motion.div 
        className="absolute top-20 left-10 opacity-20 z-0"
        animate={{ 
          y: [0, 20, 0],
          rotate: [0, 5, 0]
        }}
        transition={{ 
          duration: 8, 
          repeat: Infinity,
          repeatType: "reverse" 
        }}
      >
        <Sprout className="h-16 w-16 text-accent" />
      </motion.div>
      
      <motion.div 
        className="absolute bottom-20 right-20 opacity-20 z-0 hidden md:block"
        animate={{ 
          y: [0, -20, 0],
          rotate: [0, -5, 0]
        }}
        transition={{ 
          duration: 10, 
          repeat: Infinity,
          repeatType: "reverse" 
        }}
      >
        <Sun className="h-20 w-20 text-accent" />
      </motion.div>
    </section>
  );
};

export default HeroSection;